import React, { useCallback } from 'react';
import { Stack } from '@grnhse/seedling/lib/birch/layouts/stack_v2';
import { SecondaryBody } from '@grnhse/seedling/lib/birch/typography';
import { useFormContext } from '@grnhse/seedling/lib/birch/components/forms/form';
import { TertiaryButton } from '@grnhse/seedling/lib/birch/components/buttons';
import { HelpTooltip } from '@grnhse/seedling/lib/birch/components/tooltips';
import {
  MultiDropdown,
  useMultiDropdown,
} from '@grnhse/seedling/lib/birch/components/forms/dropdown';
import { Item } from '@grnhse/seedling/lib/birch/components/forms/dropdown/item';
import PopoverButton from 'talent_matching/components/filters/popover_button';
import { withLocalePrefix } from 'shared/utils/translation';
import { useUserContext } from 'talent_matching/contexts/user_context';
import ApplicationDate from 'talent_matching/components/filters/application_date';
import { OtherActiveApplicationsContent } from 'talent_matching/components/filters/other_active_applications';
import { PreviouslyRejectedContent } from 'talent_matching/components/filters/previously_rejected';
import { LocationContent } from 'talent_matching/components/filters/location';
import { FeedbackStatusContent } from 'talent_matching/components/filters/feedback_status';
import QuestionFiltersSection from 'talent_matching/components/filters/question_filters';
import { SCORECARD_STATUS } from 'talent_search/shared/types';
import AdvancedFilter from 'talent_matching/components/filters/advanced_filter';
import { useQueryClient } from '@tanstack/react-query';
import { standardRequestWithErrorFlash } from 'shared/utils/request';

const t = withLocalePrefix('talent_matching.filter_row');

type FilterSectionProps = {
  title: string;
  children: React.ReactNode;
  titleAdornment?: React.ReactNode;
};

const FilterSection = ({
  title,
  children,
  titleAdornment,
}: FilterSectionProps) => {
  return (
    <Stack spacing={1}>
      <Stack spacing={1} direction="row" alignItems="center">
        <SecondaryBody medium>{title}</SecondaryBody>
        {titleAdornment}
      </Stack>
      {children}
    </Stack>
  );
};

const JobPostContent = () => {
  const queryClient = useQueryClient();
  const { setValue, watch } = useFormContext();
  const {
    paths: { jobPostOptionsPath },
  } = useUserContext();
  const selectedJobPosts = (watch('jobPosts') as Item[]) || [];

  const fetchItems = useCallback(
    async (term: string) => {
      const queryKey = ['job-post-options', term];

      const items = await queryClient.fetchQuery({
        queryKey,
        queryFn: async () => {
          const response = await standardRequestWithErrorFlash.get(
            jobPostOptionsPath,
            {
              params: { term },
            }
          );

          return response.data.results as Item[];
        },
        staleTime: 1000 * 60 * 5,
      });

      return { items };
    },
    [jobPostOptionsPath, queryClient]
  );

  const dropdownProps = useMultiDropdown({
    fetchItems,
    selectedItems: selectedJobPosts,
    onSelectedItemsChange: (changes) =>
      setValue('jobPosts', changes.selectedItems, { shouldDirty: true }),
  });

  return (
    <MultiDropdown dropdownProps={dropdownProps}>
      <MultiDropdown.Field
        placeholder={t('job_post.placeholder')}
        ariaLabel={t('job_post.title')}
        condensedUnitName={t('advanced_filters.selections')}
      />
      <MultiDropdown.Flyout disablePortal />
    </MultiDropdown>
  );
};

const ApplicationFiltersGroup = () => {
  const { canUseLocations, locationFilterReadOnly } = useUserContext();
  const { watch, setValue } = useFormContext();
  const [
    appliedOnStart,
    appliedOnEnd,
    appliedOnType,
    jobPosts,
    locationFilterType,
    debouncedLocationName,
    geolocation,
    otherActiveApplications,
    excludePreviouslyRejected,
    feedbackStatus,
    questionFilters,
  ] = watch([
    'appliedOnStart',
    'appliedOnEnd',
    'appliedOnType',
    'jobPosts',
    'locationFilterType',
    'debouncedLocationName',
    'geolocation',
    'otherActiveApplications',
    'excludePreviouslyRejected',
    'feedbackStatus',
    'questionFilters',
  ]);

  const selectedJobPosts = (jobPosts as Item[]) || [];
  const hasApplicationDate = Boolean(
    appliedOnType && (appliedOnStart || appliedOnEnd)
  );
  const hasJobPostFilter = selectedJobPosts.length > 0;
  const hasOtherActiveApplications = Boolean(otherActiveApplications);
  const hasPreviouslyRejected = Boolean(excludePreviouslyRejected);
  const hasLocation =
    canUseLocations &&
    ((locationFilterType === 'radius' && geolocation) ||
      (locationFilterType === 'name' && debouncedLocationName));
  const hasFeedbackStatus = feedbackStatus !== SCORECARD_STATUS.ALL;
  const appliedQuestionFilters = (questionFilters as AdvancedFilter[]) || [];
  const questionFiltersCount = appliedQuestionFilters.filter(
    (filter: AdvancedFilter) => filter.applied()
  ).length;

  const clear = () => {
    setValue('appliedOnType', null, { shouldDirty: true });
    setValue('appliedOnStart', null, { shouldDirty: true });
    setValue('appliedOnEnd', null, { shouldDirty: true });
    setValue('jobPosts', [], { shouldDirty: true });
    setValue('excludePreviouslyRejected', false, { shouldDirty: true });
    setValue('otherActiveApplications', null, { shouldDirty: true });
    setValue('feedbackStatus', SCORECARD_STATUS.ALL, { shouldDirty: true });
    setValue('questionFilters', [], { shouldDirty: true });
    if (!locationFilterReadOnly) {
      setValue('geolocation', null, { shouldDirty: true });
      setValue('geolocationRadius', '5', { shouldDirty: true });
      setValue('locationFilterType', 'radius', { shouldDirty: true });
      setValue('locationName', '', { shouldDirty: true });
      setValue('debouncedLocationName', '', { shouldDirty: true });
    }
  };
  const appliedCount =
    questionFiltersCount +
    [
      hasApplicationDate,
      hasJobPostFilter,
      hasOtherActiveApplications,
      hasPreviouslyRejected,
      hasLocation,
      hasFeedbackStatus,
    ].filter(Boolean).length;

  const buttonLabel = `${t('grouped_buttons.application')}${
    appliedCount ? ` (${appliedCount})` : ''
  }`;

  return (
    <PopoverButton
      buttonLabel={buttonLabel}
      title={t('grouped_buttons.application_filters')}
      width="700px"
    >
      <Stack spacing={3}>
        <FilterSection title={t('application_date.title')}>
          <ApplicationDate />
        </FilterSection>
        {canUseLocations && (
          <FilterSection
            title={t('location.title')}
            titleAdornment={
              <HelpTooltip
                label={t('location.application_tooltip')}
                ariaLabel={t('location.application_tooltip')}
              />
            }
          >
            <LocationContent readOnly={locationFilterReadOnly} />
          </FilterSection>
        )}
        <FilterSection title={t('previously_rejected.title')}>
          <PreviouslyRejectedContent />
        </FilterSection>
        <FilterSection title={t('other_active_applications.title')}>
          <OtherActiveApplicationsContent />
        </FilterSection>
        <FilterSection title={t('feedback_status.title')}>
          <FeedbackStatusContent />
        </FilterSection>
        <FilterSection title={t('job_post.title')}>
          <JobPostContent />
        </FilterSection>
        <QuestionFiltersSection />
        <Stack sx={{ alignSelf: 'flex-start' }}>
          <TertiaryButton onClick={clear} size="lg">
            {t('grouped_buttons.clear')}
          </TertiaryButton>
        </Stack>
      </Stack>
    </PopoverButton>
  );
};

export default ApplicationFiltersGroup;
